// ============================================
// File: src/components/Navbar.js
// ============================================
import React, { useState, useEffect } from 'react';
import logo from '../assets/logoRR.png';

// ============================================
// Bilingual translations for AR/EN support
// ============================================
const translations = {
  ar: {
    navHome: "الرئيسية", 
    navAbout: "من نحن", 
    navServices: "خدماتنا",
    navFeatures: "المميزات", 
    navFaq: "الأسئلة", 
    navContact: "اتصل بنا",
    logoSub: "إعادة تدوير ذكية لمستقبل أخضر", 
    navGallery: "معرض الصور",
    brandName: "رفاد التدوير"
  },
  en: {
    navHome: "Home", 
    navAbout: "About", 
    navServices: "Services",
    navFeatures: "Features", 
    navFaq: "FAQ", 
    navContact: "Contact",
    logoSub: "Smart Recycling for a Green Future", 
    navGallery: "Gallery",
    brandName: "Rafad Recycling"
    
  }
};

// ============================================
// Navbar component with responsive sidebar and language toggle
// ============================================
const Navbar = ({ currentLang, toggleLang }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const t = translations[currentLang];
  const isRTL = currentLang === 'ar';

  // ============================================
  // Handle scroll events for navbar style and active section detection
  // ============================================
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);

      const sections = document.querySelectorAll('section');
      let current = '';
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 100;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop && window.scrollY < sectionTop + sectionHeight) {
          current = section.getAttribute('id');
        }
      });
      setActiveSection(current);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ============================================
  // Smooth scroll to section by ID
  // ============================================
  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setSidebarOpen(false);
  };

  // ============================================
  // Navigation items - Correct order for each language
  // For Arabic: Home -> About -> Services -> Gallery -> Features -> FAQ -> Contact
  // For English: Home -> About -> Services -> Gallery -> Features -> FAQ -> Contact
  // ============================================
  const navItems = [
    { id: 'home', label: t.navHome, icon: 'fas fa-home' },
    { id: 'about', label: t.navAbout, icon: 'fas fa-info-circle' },
    { id: 'services', label: t.navServices, icon: 'fas fa-recycle' },
    { id: 'gallery', label: t.navGallery, icon: 'fas fa-images' },
    { id: 'features', label: t.navFeatures, icon: 'fas fa-star' },
    { id: 'faq', label: t.navFaq, icon: 'fas fa-question-circle' },
    { id: 'contact', label: t.navContact, icon: 'fas fa-envelope' }
  ];

  return (
    <>
      {/* Main Navigation Bar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="logo">
          <img src={logo} alt="Rafad Logo" onError={(e) => e.target.style.display = 'none'} />
          <div className="logo-text">
            <h1>{t.brandName}</h1>
            <p>{t.logoSub}</p>
          </div>
        </div>
        
        {/* Desktop Navigation Links */}
        <ul className="nav-links">
          {navItems.map(item => (
            <li key={item.id}>
              <a 
                href={`#${item.id}`} 
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}
              >
                {item.label}
              </a>
            </li>
          ))}
          <li className="control-buttons">
            <button className="lang-toggle" onClick={toggleLang}>
              <i className="fas fa-globe"></i> {currentLang === 'ar' ? 'EN' : 'AR'}
            </button>
          </li>
        </ul>
        
        {/* Mobile Menu Button */}
        <button className="menu-toggle-btn" onClick={() => setSidebarOpen(true)}>
          <i className="fas fa-bars"></i> {t.menuButton}
        </button>
      </nav>

      {/* Sidebar for Mobile Devices */}
      <div className={`side-navbar ${sidebarOpen ? 'open' : ''} ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="side-navbar-header">
          <h3>{t.brandName}</h3>
          <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <ul className="side-nav-links">
          {navItems.map(item => (
            <li key={item.id}>
              <a href={`#${item.id}`} onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}>
                <i className={item.icon}></i> <span>{item.label}</span>
              </a>
            </li>
          ))}
        </ul>
        <button className="side-lang-toggle" onClick={toggleLang}>
          <i className="fas fa-globe"></i> <span>{currentLang === 'ar' ? 'EN' : 'AR'}</span>
        </button>
      </div>
      
      {/* Sidebar Overlay */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}></div>

      {/* Component Styles */}
      <style>{`
        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 5%;
          flex-wrap: wrap;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          /* Fully transparent at start */
          background: transparent;
          backdrop-filter: none;
          border-bottom: none;
          box-shadow: none;
          transition: all 0.4s ease;
        }
        
        /* RTL Layout for Arabic */
        .navbar.rtl {
          direction: rtl;
        }
        
        /* LTR Layout for English */
        .navbar.ltr {
          direction: ltr;
        }
        
        /* On scroll - becomes semi-transparent with glass effect */
        .navbar.scrolled {
          padding: 0.8rem 5%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(83, 176, 150, 0.2);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }
        
        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .logo img {
          height: 55px;
          width: auto;
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .logo img {
          height: 42px;
        }
        
        .logo-text h1 {
          font-size: 1.6rem;
          font-weight: 800;
          background: linear-gradient(135deg, #ffffff, #53b096);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .logo-text h1 {
          font-size: 1.3rem;
          background: linear-gradient(135deg, #1a2a3a, #53b096);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        .logo-text p {
          font-size: 0.65rem;
          color: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .logo-text p {
          color: #6a7a8a;
        }
        
        .nav-links {
          display: flex;
          gap: 2rem;
          list-style: none;
          align-items: center;
        }
        
        .nav-links a {
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          color: white;
          cursor: pointer;
          position: relative;
          padding-bottom: 5px;
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .nav-links a {
          color: #1a2a3a;
        }
        
        .nav-links a:hover, 
        .nav-links a.active {
          color: #71b887;
        }
        
        .navbar.scrolled .nav-links a:hover, 
        .navbar.scrolled .nav-links a.active {
          color: #53b096;
        }
        
        .nav-links a.active::after {
          content: '';
          position: absolute;
          bottom: -5px;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, #71b887, #53b096);
          border-radius: 2px;
        }
        
        /* RTL specific styles for active indicator */
        .navbar.rtl .nav-links a.active::after {
          left: 0;
          right: 0;
        }
        
        .navbar.ltr .nav-links a.active::after {
          left: 0;
          right: 0;
        }
        
        .lang-toggle {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 40px;
          padding: 0.5rem 1.2rem;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
          color: white;
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .lang-toggle {
          background: rgba(83, 176, 150, 0.1);
          border: 1px solid rgba(83, 176, 150, 0.3);
          color: #53b096;
        }
        
        .lang-toggle:hover {
          background: rgba(113, 184, 135, 0.3);
          border-color: #71b887;
        }
        
        .menu-toggle-btn {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 40px;
          padding: 0.5rem 1rem;
          cursor: pointer;
          color: white;
          display: none;
          transition: all 0.3s ease;
        }
        
        .navbar.scrolled .menu-toggle-btn {
          background: rgba(83, 176, 150, 0.1);
          border: 1px solid rgba(83, 176, 150, 0.3);
          color: #53b096;
        }
        
        /* Sidebar Styles */
        .side-navbar {
          position: fixed;
          top: 0;
          width: 300px;
          height: 100%;
          background: #ffffff;
          box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
          z-index: 200;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          padding: 2rem 1.5rem;
        }
        
        /* RTL Sidebar - opens from right */
        .side-navbar.rtl {
          right: -320px;
          left: auto;
        }
        
        .side-navbar.rtl.open {
          right: 0;
          left: auto;
        }
        
        /* LTR Sidebar - opens from left */
        .side-navbar.ltr {
          left: -320px;
          right: auto;
        }
        
        .side-navbar.ltr.open {
          left: 0;
          right: auto;
        }
        
        /* RTL Sidebar Header */
        .side-navbar.rtl .side-navbar-header {
          flex-direction: row;
        }
        
        /* LTR Sidebar Header */
        .side-navbar.ltr .side-navbar-header {
          flex-direction: row-reverse;
        }
        
        .side-navbar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          border-bottom: 2px solid rgba(83, 176, 150, 0.2);
          padding-bottom: 1rem;
        }
        
        .side-navbar-header h3 {
          color: #1a2a3a;
        }
        
        .close-sidebar {
          background: rgba(83, 176, 150, 0.1);
          border: none;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          cursor: pointer;
          color: #53b096;
          transition: transform 0.3s ease;
        }
        
        .close-sidebar:hover {
          background: rgba(83, 176, 150, 0.2);
          transform: rotate(90deg);
        }
        
        .side-nav-links {
          list-style: none;
          flex: 1;
        }
        
        .side-nav-links li { margin: 0.8rem 0; }
        
        .side-nav-links a {
          text-decoration: none;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a2a3a;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0.5rem;
          border-radius: 10px;
        }
        
        /* RTL Sidebar Links - icon on right, text on left */
        .side-navbar.rtl .side-nav-links a {
          flex-direction: row-reverse;
        }
        
        /* LTR Sidebar Links - icon on left, text on right */
        .side-navbar.ltr .side-nav-links a {
          flex-direction: row;
        }
        
        .side-nav-links a:hover {
          background: rgba(83, 176, 150, 0.1);
          color: #53b096;
        }
        
        .side-nav-links a i { color: #53b096; width: 24px; }
        
        .side-lang-toggle {
          background: rgba(83, 176, 150, 0.1);
          border: 1px solid rgba(83, 176, 150, 0.3);
          border-radius: 50px;
          padding: 0.8rem;
          cursor: pointer;
          color: #53b096;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(4px);
          z-index: 150;
          visibility: hidden;
          opacity: 0;
          transition: all 0.3s ease;
        }
        
        .sidebar-overlay.active {
          visibility: visible;
          opacity: 1;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .nav-links { display: none; }
          .menu-toggle-btn { display: flex; align-items: center; gap: 8px; }
          .control-buttons .lang-toggle { display: none; }
          .navbar { padding: 1rem 5%; }
          .navbar.scrolled { padding: 0.7rem 5%; }
        }
        
        @media (min-width: 769px) {
          .menu-toggle-btn { display: none; }
        }
      `}</style>
    </>
  );
};

export default Navbar;